import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPostFormData,
  apiPut,
} from "@apps/dashboard/lib/api-fetch";
import type {
  AcceptHouseInvitationResult,
  BudgetAllocation,
  BudgetCopyPreviewResponse,
  BudgetMonthReadiness,
  CreateExpenseCategoryPayload,
  CreateExpensePayload,
  CreateReceiptScanPayload,
  CreateRecurringExpensesPayload,
  CreateVaultItemPayload,
  DashboardAnalytics,
  DashboardSummary,
  ExpenseCategory,
  ExpenseDetail,
  ExpenseListItem,
  ExpenseListQuery,
  ExpenseSeriesDetail,
  FinancingConfirmedResponse,
  FinancingPreviewResponse,
  FinancingUpdatePayload,
  FinancingUpsertPayload,
  HouseFinancingDetail,
  HouseFinancingListItem,
  PatchExpenseSeriesPayload,
  PatchVaultItemPayload,
  HouseFinancialProfile,
  HouseInvitationCreated,
  HouseInvitationPendingItem,
  HouseInvitationPreview,
  MyPendingHouseInvitationItem,
  HouseListItem,
  HouseMemberWithUser,
  HouseResponse,
  NotificationListResponse,
  NotificationPreferences,
  NotificationUnreadCountResponse,
  PatchExpensePayload,
  PatchReceiptScanPayload,
  PutBudgetMonthPayload,
  PushDeviceItem,
  ReceiptScan,
  RecurringExpensesCreated,
  RegisterPushDevicePayload,
  StorageDownloadUrlResponse,
  StorageUploadResponse,
  VaultItem,
} from "@apps/dashboard/lib/home-expense-types";
import type { Locale } from "@project/i18n";

export const homeExpenseQueryKeys = {
  accountOnboarding: (locale: Locale) => ["account", "onboarding", locale] as const,
  houses: (locale: Locale) => ["houses", locale] as const,
  houseMembers: (locale: Locale, houseId: string) => ["houses", houseId, "members", locale] as const,
  houseInvitations: (locale: Locale, houseId: string) =>
    ["houses", houseId, "invitations", locale] as const,
  invitationPreview: (locale: Locale, token: string) =>
    ["house-invitations", "preview", token, locale] as const,
  myPendingInvitations: (locale: Locale) =>
    ["house-invitations", "pending", locale] as const,
  financialProfile: (locale: Locale, houseId: string) =>
    ["houses", houseId, "financial-profile", locale] as const,
  dashboardSummary: (locale: Locale, houseId: string, yearMonth?: string) =>
    ["houses", houseId, "dashboard", "summary", yearMonth ?? "current", locale] as const,
  dashboardAnalytics: (
    locale: Locale,
    houseId: string,
    opts?: { months?: number; minMonthsForInsight?: number },
  ) =>
    [
      "houses",
      houseId,
      "dashboard",
      "analytics",
      opts?.months ?? 12,
      opts?.minMonthsForInsight ?? 6,
      locale,
    ] as const,
  expenses: (locale: Locale, houseId: string, q: ExpenseListQuery) =>
    ["houses", houseId, "expenses", q, locale] as const,
  expense: (locale: Locale, houseId: string, expenseId: string) =>
    ["houses", houseId, "expenses", expenseId, locale] as const,
  expenseSeries: (locale: Locale, houseId: string, seriesId: string) =>
    ["houses", houseId, "expense-series", seriesId, locale] as const,
  expenseSeriesList: (locale: Locale, houseId: string) =>
    ["houses", houseId, "expense-series", "list", locale] as const,
  categories: (locale: Locale, houseId: string) => ["houses", houseId, "categories", locale] as const,
  budgetMonth: (locale: Locale, houseId: string, yearMonth: string) =>
    ["houses", houseId, "budgets", yearMonth, locale] as const,
  vault: (locale: Locale, houseId: string) => ["houses", houseId, "vault", locale] as const,
  vaultItem: (locale: Locale, houseId: string, itemId: string) =>
    ["houses", houseId, "vault", itemId, locale] as const,
  receiptScans: (locale: Locale, houseId: string) => ["houses", houseId, "receipt-scans", locale] as const,
  receiptScan: (locale: Locale, houseId: string, scanId: string) =>
    ["houses", houseId, "receipt-scans", scanId, locale] as const,
  financingsList: (locale: Locale, houseId: string) =>
    ["houses", houseId, "financings", locale] as const,
  financingDetail: (locale: Locale, houseId: string, financingId: string) =>
    ["houses", houseId, "financings", financingId, locale] as const,
  notificationsInbox: (locale: Locale) => ["notifications", "inbox", locale] as const,
  notificationUnread: (locale: Locale) => ["notifications", "unread", locale] as const,
  pushDevices: (locale: Locale) => ["push-devices", locale] as const,
  notificationPreferences: (locale: Locale) => ["notification-preferences", locale] as const,
};

export function fetchNotifications(
  locale: Locale,
  opts: { limit?: number; cursor?: string | null } = {},
): Promise<NotificationListResponse> {
  const params = new URLSearchParams();
  if (opts.limit != null) params.set("limit", String(opts.limit));
  if (opts.cursor) params.set("cursor", opts.cursor);
  const q = params.toString();
  return apiGet<NotificationListResponse>(q ? `/notifications?${q}` : "/notifications", locale);
}

export function fetchNotificationUnreadCount(locale: Locale): Promise<NotificationUnreadCountResponse> {
  return apiGet<NotificationUnreadCountResponse>("/notifications/unread-count", locale);
}

export function markNotificationRead(locale: Locale, id: string): Promise<{ ok: true }> {
  return apiPatch<{ ok: true }>(`/notifications/${id}/read`, {}, locale);
}

export function markAllNotificationsRead(locale: Locale): Promise<{ ok: true }> {
  return apiPost<{ ok: true }>("/notifications/read-all", {}, locale);
}

export function fetchPushDevices(locale: Locale): Promise<PushDeviceItem[]> {
  return apiGet<PushDeviceItem[]>("/push-devices", locale);
}

export function registerPushDevice(
  locale: Locale,
  body: RegisterPushDevicePayload,
): Promise<PushDeviceItem> {
  return apiPost<PushDeviceItem>("/push-devices", body, locale);
}

export function deletePushDevice(locale: Locale, deviceId: string): Promise<{ ok: true }> {
  return apiDelete<{ ok: true }>(`/push-devices/${deviceId}`, locale);
}

export function patchPushDeviceEnabled(
  locale: Locale,
  deviceId: string,
  pushEnabled: boolean,
): Promise<PushDeviceItem> {
  return apiPatch<PushDeviceItem>(`/push-devices/${deviceId}`, { pushEnabled }, locale);
}

export function fetchNotificationPreferences(locale: Locale): Promise<NotificationPreferences> {
  return apiGet<NotificationPreferences>("/notification-preferences", locale);
}

export function patchNotificationPreferences(
  locale: Locale,
  patch: Partial<NotificationPreferences>,
): Promise<NotificationPreferences> {
  return apiPatch<NotificationPreferences>("/notification-preferences", patch, locale);
}

export interface AccountOnboardingState {
  onboardingCompletedAt: string | null;
}

export function fetchAccountOnboarding(locale: Locale): Promise<AccountOnboardingState> {
  return apiGet<AccountOnboardingState>("/account/onboarding", locale);
}

export function completeAccountOnboarding(
  locale: Locale,
): Promise<{ ok: true; onboardingCompletedAt: string }> {
  return apiPost<{ ok: true; onboardingCompletedAt: string }>(
    "/account/onboarding/complete",
    {},
    locale,
  );
}

export function deleteUserAccount(locale: Locale): Promise<{ ok: true }> {
  return apiDelete<{ ok: true }>("/account", locale);
}

export function fetchHouses(locale: Locale): Promise<HouseListItem[]> {
  return apiGet<HouseListItem[]>("/houses", locale);
}

export function createHouse(locale: Locale, name: string): Promise<HouseResponse> {
  return apiPost<HouseResponse>("/houses", { name }, locale);
}

export function updateHouse(
  locale: Locale,
  houseId: string,
  body: { name: string },
): Promise<HouseResponse> {
  return apiPatch<HouseResponse>(`/houses/${houseId}`, body, locale);
}

export function deleteHouse(locale: Locale, houseId: string): Promise<{ ok: boolean }> {
  return apiDelete<{ ok: boolean }>(`/houses/${houseId}`, locale);
}

export function fetchHouseMembers(locale: Locale, houseId: string): Promise<HouseMemberWithUser[]> {
  return apiGet<HouseMemberWithUser[]>(`/houses/${houseId}/members`, locale);
}

export function updateHouseMember(
  locale: Locale,
  houseId: string,
  targetUserId: string,
  body: { role?: "owner" | "admin" | "member"; defaultSplitBps?: number | null },
): Promise<unknown> {
  return apiPatch<unknown>(`/houses/${houseId}/members/${targetUserId}`, body, locale);
}

export function removeHouseMember(
  locale: Locale,
  houseId: string,
  targetUserId: string,
): Promise<{ ok: boolean }> {
  return apiDelete<{ ok: boolean }>(`/houses/${houseId}/members/${targetUserId}`, locale);
}

export function fetchInvitationPreview(
  locale: Locale,
  token: string,
): Promise<HouseInvitationPreview> {
  const q = `?token=${encodeURIComponent(token)}`;
  return apiGet<HouseInvitationPreview>(`/house-invitations/preview${q}`, locale);
}

export function fetchMyPendingHouseInvitations(
  locale: Locale,
): Promise<MyPendingHouseInvitationItem[]> {
  return apiGet<MyPendingHouseInvitationItem[]>("/house-invitations/pending", locale);
}

export function createHouseInvitation(
  locale: Locale,
  houseId: string,
  body: { email: string; role?: "admin" | "member" },
): Promise<HouseInvitationCreated> {
  return apiPost<HouseInvitationCreated>(
    `/houses/${houseId}/invitations`,
    body,
    locale,
  );
}

export function fetchHouseInvitations(
  locale: Locale,
  houseId: string,
): Promise<HouseInvitationPendingItem[]> {
  return apiGet<HouseInvitationPendingItem[]>(
    `/houses/${houseId}/invitations`,
    locale,
  );
}

export function revokeHouseInvitation(
  locale: Locale,
  houseId: string,
  invitationId: string,
): Promise<{ ok: boolean }> {
  return apiDelete<{ ok: boolean }>(
    `/houses/${houseId}/invitations/${invitationId}`,
    locale,
  );
}

export function acceptHouseInvitation(
  locale: Locale,
  body: { token: string },
): Promise<AcceptHouseInvitationResult> {
  return apiPost<AcceptHouseInvitationResult>(
    "/house-invitations/accept",
    body,
    locale,
  );
}

export function acceptHouseInvitationById(
  locale: Locale,
  body: { invitationId: string },
): Promise<AcceptHouseInvitationResult> {
  return apiPost<AcceptHouseInvitationResult>(
    "/house-invitations/accept-by-id",
    body,
    locale,
  );
}

export function fetchFinancialProfile(
  locale: Locale,
  houseId: string,
): Promise<HouseFinancialProfile> {
  return apiGet<HouseFinancialProfile>(`/houses/${houseId}/financial-profile`, locale);
}

export function patchFinancialProfile(
  locale: Locale,
  houseId: string,
  body: {
    openingBankBalanceMinor?: number;
    currency?: string;
    monthlyBudgetCapMinor?: number | null;
  },
): Promise<HouseFinancialProfile> {
  return apiPatch<HouseFinancialProfile>(`/houses/${houseId}/financial-profile`, body, locale);
}

export function fetchDashboardSummary(
  locale: Locale,
  houseId: string,
  yearMonth?: string,
): Promise<DashboardSummary> {
  const qs = yearMonth ? `?yearMonth=${encodeURIComponent(yearMonth)}` : "";
  return apiGet<DashboardSummary>(`/houses/${houseId}/dashboard/summary${qs}`, locale);
}

export function fetchDashboardAnalytics(
  locale: Locale,
  houseId: string,
  opts?: { months?: number; minMonthsForInsight?: number },
): Promise<DashboardAnalytics> {
  const params = new URLSearchParams();
  if (opts?.months != null) params.set("months", String(opts.months));
  if (opts?.minMonthsForInsight != null) {
    params.set("minMonthsForInsight", String(opts.minMonthsForInsight));
  }
  const q = params.toString();
  return apiGet<DashboardAnalytics>(
    `/houses/${houseId}/dashboard/analytics${q ? `?${q}` : ""}`,
    locale,
  );
}

function buildExpenseQueryString(q: ExpenseListQuery): string {
  const p = new URLSearchParams();
  if (q.from) p.set("from", q.from);
  if (q.to) p.set("to", q.to);
  if (q.categoryId) p.set("categoryId", q.categoryId);
  if (q.contributionScope) p.set("contributionScope", q.contributionScope);
  if (q.paid) p.set("paid", q.paid);
  if (q.search) p.set("search", q.search);
  if (q.sort) p.set("sort", q.sort);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function fetchExpenses(
  locale: Locale,
  houseId: string,
  query: ExpenseListQuery = {},
): Promise<ExpenseListItem[]> {
  const qs = buildExpenseQueryString({ sort: "date_desc", ...query });
  return apiGet<ExpenseListItem[]>(`/houses/${houseId}/expenses${qs}`, locale);
}

export function fetchExpense(
  locale: Locale,
  houseId: string,
  expenseId: string,
): Promise<ExpenseDetail> {
  return apiGet<ExpenseDetail>(`/houses/${houseId}/expenses/${expenseId}`, locale);
}

export function createExpense(
  locale: Locale,
  houseId: string,
  body: CreateExpensePayload,
): Promise<ExpenseDetail> {
  return apiPost<ExpenseDetail>(`/houses/${houseId}/expenses`, body, locale);
}

export function createRecurringExpenses(
  locale: Locale,
  houseId: string,
  body: CreateRecurringExpensesPayload,
): Promise<RecurringExpensesCreated> {
  return apiPost<RecurringExpensesCreated>(
    `/houses/${houseId}/expenses/recurring`,
    body,
    locale,
  );
}

export function fetchExpenseSeriesList(
  locale: Locale,
  houseId: string,
): Promise<ExpenseSeriesDetail[]> {
  return apiGet<ExpenseSeriesDetail[]>(`/houses/${houseId}/expense-series`, locale);
}

export function fetchExpenseSeries(
  locale: Locale,
  houseId: string,
  seriesId: string,
): Promise<ExpenseSeriesDetail> {
  return apiGet<ExpenseSeriesDetail>(
    `/houses/${houseId}/expense-series/${seriesId}`,
    locale,
  );
}

export function patchExpenseSeries(
  locale: Locale,
  houseId: string,
  seriesId: string,
  body: PatchExpenseSeriesPayload,
): Promise<ExpenseSeriesDetail> {
  return apiPatch<ExpenseSeriesDetail>(
    `/houses/${houseId}/expense-series/${seriesId}`,
    body,
    locale,
  );
}

export function patchExpense(
  locale: Locale,
  houseId: string,
  expenseId: string,
  body: PatchExpensePayload,
): Promise<ExpenseDetail> {
  return apiPatch<ExpenseDetail>(`/houses/${houseId}/expenses/${expenseId}`, body, locale);
}

export function markExpensePaid(
  locale: Locale,
  houseId: string,
  expenseId: string,
): Promise<ExpenseDetail> {
  return apiPost<ExpenseDetail>(
    `/houses/${houseId}/expenses/${expenseId}/mark-paid`,
    {},
    locale,
  );
}

export function markExpenseUnpaid(
  locale: Locale,
  houseId: string,
  expenseId: string,
): Promise<ExpenseDetail> {
  return apiPost<ExpenseDetail>(
    `/houses/${houseId}/expenses/${expenseId}/mark-unpaid`,
    {},
    locale,
  );
}

export function deleteExpense(
  locale: Locale,
  houseId: string,
  expenseId: string,
): Promise<{ ok: boolean }> {
  return apiDelete<{ ok: boolean }>(`/houses/${houseId}/expenses/${expenseId}`, locale);
}

export function fetchExpenseCategories(
  locale: Locale,
  houseId: string,
): Promise<ExpenseCategory[]> {
  return apiGet<ExpenseCategory[]>(`/houses/${houseId}/categories`, locale);
}

export function createExpenseCategory(
  locale: Locale,
  houseId: string,
  body: CreateExpenseCategoryPayload,
): Promise<ExpenseCategory> {
  return apiPost<ExpenseCategory>(`/houses/${houseId}/categories`, body, locale);
}

export function fetchBudgetMonth(
  locale: Locale,
  houseId: string,
  yearMonth: string,
): Promise<BudgetAllocation[]> {
  return apiGet<BudgetAllocation[]>(
    `/houses/${houseId}/budgets/${encodeURIComponent(yearMonth)}`,
    locale,
  );
}

export function replaceBudgetMonth(
  locale: Locale,
  houseId: string,
  yearMonth: string,
  body: PutBudgetMonthPayload,
): Promise<BudgetAllocation[]> {
  return apiPut<BudgetAllocation[]>(
    `/houses/${houseId}/budgets/${encodeURIComponent(yearMonth)}`,
    body,
    locale,
  );
}

export function fetchBudgetMonthReadiness(
  locale: Locale,
  houseId: string,
  yearMonth: string,
): Promise<BudgetMonthReadiness> {
  return apiGet<BudgetMonthReadiness>(
    `/houses/${houseId}/budgets/${encodeURIComponent(yearMonth)}/readiness`,
    locale,
  );
}

export function fetchBudgetCopyPreview(
  locale: Locale,
  houseId: string,
  yearMonth: string,
  fromYearMonth: string,
): Promise<BudgetCopyPreviewResponse> {
  const q = `from=${encodeURIComponent(fromYearMonth)}`;
  return apiGet<BudgetCopyPreviewResponse>(
    `/houses/${houseId}/budgets/${encodeURIComponent(yearMonth)}/copy-preview?${q}`,
    locale,
  );
}

export function copyBudgetMonthFrom(
  locale: Locale,
  houseId: string,
  yearMonth: string,
  body: { sourceYearMonth: string; replaceExisting: boolean },
): Promise<{ copied: number; yearMonth: string }> {
  return apiPost<{ copied: number; yearMonth: string }>(
    `/houses/${houseId}/budgets/${encodeURIComponent(yearMonth)}/copy-from`,
    body,
    locale,
  );
}

export function fetchVaultItems(locale: Locale, houseId: string): Promise<VaultItem[]> {
  return apiGet<VaultItem[]>(`/houses/${houseId}/vault`, locale);
}

export function fetchVaultItem(
  locale: Locale,
  houseId: string,
  itemId: string,
): Promise<VaultItem> {
  return apiGet<VaultItem>(`/houses/${houseId}/vault/${itemId}`, locale);
}

export function createVaultItem(
  locale: Locale,
  houseId: string,
  body: CreateVaultItemPayload,
): Promise<VaultItem> {
  return apiPost<VaultItem>(`/houses/${houseId}/vault`, body, locale);
}

export function patchVaultItem(
  locale: Locale,
  houseId: string,
  itemId: string,
  body: PatchVaultItemPayload,
): Promise<VaultItem> {
  return apiPatch<VaultItem>(`/houses/${houseId}/vault/${itemId}`, body, locale);
}

export function deleteVaultItem(
  locale: Locale,
  houseId: string,
  itemId: string,
): Promise<{ ok: boolean }> {
  return apiDelete<{ ok: boolean }>(`/houses/${houseId}/vault/${itemId}`, locale);
}

export function fetchReceiptScans(locale: Locale, houseId: string): Promise<ReceiptScan[]> {
  return apiGet<ReceiptScan[]>(`/houses/${houseId}/receipt-scans`, locale);
}

export function fetchReceiptScan(
  locale: Locale,
  houseId: string,
  scanId: string,
): Promise<ReceiptScan> {
  return apiGet<ReceiptScan>(`/houses/${houseId}/receipt-scans/${scanId}`, locale);
}

export function createReceiptScan(
  locale: Locale,
  houseId: string,
  body: CreateReceiptScanPayload,
): Promise<ReceiptScan> {
  return apiPost<ReceiptScan>(`/houses/${houseId}/receipt-scans`, body, locale);
}

export function patchReceiptScan(
  locale: Locale,
  houseId: string,
  scanId: string,
  body: PatchReceiptScanPayload,
): Promise<ReceiptScan> {
  return apiPatch<ReceiptScan>(`/houses/${houseId}/receipt-scans/${scanId}`, body, locale);
}

export function uploadStorageFile(locale: Locale, file: File): Promise<StorageUploadResponse> {
  const fd = new FormData();
  fd.set("file", file);
  return apiPostFormData<StorageUploadResponse>("/storage/upload", fd, locale);
}

export function fetchStorageDownloadUrl(
  locale: Locale,
  key: string,
  expiresInSeconds?: number,
): Promise<StorageDownloadUrlResponse> {
  const params = new URLSearchParams({ key });
  if (expiresInSeconds !== undefined) {
    params.set("expiresIn", String(expiresInSeconds));
  }
  return apiGet<StorageDownloadUrlResponse>(`/storage/download-url?${params.toString()}`, locale);
}

/** Obtiene URL firmada y abre el documento en una pestaña nueva. */
export async function openStorageDocumentInNewTab(locale: Locale, key: string): Promise<void> {
  const { url } = await fetchStorageDownloadUrl(locale, key);
  window.open(url, "_blank", "noopener,noreferrer");
}

export function previewFinancing(
  locale: Locale,
  houseId: string,
  body: FinancingUpsertPayload,
): Promise<FinancingPreviewResponse> {
  return apiPost<FinancingPreviewResponse>(`/houses/${houseId}/financings/preview`, body, locale);
}

export function confirmFinancing(
  locale: Locale,
  houseId: string,
  body: FinancingUpsertPayload,
): Promise<FinancingConfirmedResponse> {
  return apiPost<FinancingConfirmedResponse>(`/houses/${houseId}/financings/confirm`, body, locale);
}

export function fetchFinancings(
  locale: Locale,
  houseId: string,
): Promise<HouseFinancingListItem[]> {
  return apiGet<HouseFinancingListItem[]>(`/houses/${houseId}/financings`, locale);
}

export function fetchFinancing(
  locale: Locale,
  houseId: string,
  financingId: string,
): Promise<HouseFinancingDetail> {
  return apiGet<HouseFinancingDetail>(
    `/houses/${houseId}/financings/${financingId}`,
    locale,
  );
}

export function patchFinancing(
  locale: Locale,
  houseId: string,
  financingId: string,
  body: FinancingUpdatePayload,
): Promise<HouseFinancingDetail> {
  return apiPatch<HouseFinancingDetail>(
    `/houses/${houseId}/financings/${financingId}`,
    body,
    locale,
  );
}

export async function deleteFinancing(
  locale: Locale,
  houseId: string,
  financingId: string,
): Promise<void> {
  await apiDelete<unknown>(`/houses/${houseId}/financings/${financingId}`, locale);
}
